import { useEffect } from "react";
import { isSearchInFlight } from "@/types/api";
import { X, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useStartEntityTimeline, useEntityTimeline } from "@/hooks/use-api";
import { formatAbsoluteTime } from "@/lib/formatters";

interface EntityTimelineProps {
  entityType: string | null;
  entityId: string | null;
  onClose: () => void;
}

export function EntityTimeline({
  entityType,
  entityId,
  onClose,
}: EntityTimelineProps) {
  const startMutation = useStartEntityTimeline();

  const executionId = startMutation.data?.execution_id ?? null;
  const timelineQuery = useEntityTimeline(executionId, entityId ?? "", entityType ?? "");

  // Start timeline query when entity is selected
  useEffect(() => {
    if (entityType && entityId) {
      startMutation.mutate({ entityType, entityId });
    }
  }, [entityType, entityId]);

  // Escape key
  useEffect(() => {
    if (!entityId) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [entityId, onClose]);

  if (!entityType || !entityId) return null;

  const result = startMutation.data?.execution_id === null
    ? { status: "SUCCEEDED" as const, timeline: startMutation.data.timeline, error: null }
    : timelineQuery.data;
  const isLoading =
    startMutation.isPending ||
    !result ||
    isSearchInFlight(result.status);

  const rows = result?.timeline ?? [];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${entityType} timeline: ${entityId}`}
        className="fixed right-0 top-0 z-50 flex h-full w-[480px] max-w-full flex-col border-l bg-background shadow-xl animate-in slide-in-from-right"
      >
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <h2 className="text-lg font-semibold">Entity Timeline</h2>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="secondary" className="font-mono text-xs">
                {entityType}
              </Badge>
              <span className="text-sm font-mono text-muted-foreground">
                {entityId}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading timeline...</span>
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : result?.status === "FAILED" ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              Failed to load timeline: {result.error ?? "Unknown error"}
            </div>
          ) : result?.status === "CANCELLED" ? (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-400">
              Timeline query was cancelled.
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              No events found for this entity
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex gap-3 rounded-lg border p-3 text-sm"
                >
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {row.time
                          ? formatAbsoluteTime(String(row.time))
                          : `Event ${idx + 1}`}
                      </span>
                      {row.class_name != null && (
                        <Badge variant="secondary" className="text-xs">
                          {String(row.class_name)}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 font-medium">
                      {String(
                        (row.activity_name as string) ??
                          (row.action as string) ??
                          (row.type_name as string) ??
                          "Event",
                      )}
                    </p>
                    {row.description != null && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {String(row.description)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
